import * as React from 'react';
import { observer } from 'mobx-react';
import { action, computed, observable } from 'mobx';
import { Button, ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';

import { ApiUrls, DateTime } from '@app/AppConstants';
import { CompletionType, PromiseCompletion } from '@app/Classes/PromiseCompletion';
import { QueueStatisticsModel, SelfTestResultStatus } from '@app/Models/WebApiModels';

import ApiService from '@app/Services/ApiService';
import { DateTimeService } from '@app/Services/DateTimeService';
import { downloadService } from '@app/Services/DownloadService';
import Timer from '@app/Services/Timer';

import { Icon } from '@app/Components/Icon';
import { ToolTipItem } from '@app/Components/ToolTipItem';
import { modalService } from '@app/Components/Modal';
import { SelfTestBadge } from '../SelfTestBadge/SelfTestBadge';
const ManageMessagesDialog = React.lazy(() => import('../Dialogs/ManageMessagesDialog'));

type SelfTestQueueProps = {
    queueName: string;
};

@observer
export class SelfTestQueue extends React.Component<SelfTestQueueProps> {
    readonly _store: SelfTestQueueStore;

    constructor(props: SelfTestQueueProps) {
        super(props);
        this._store = new SelfTestQueueStore(props.queueName);
    }

    componentDidMount(): void {
        void this._store.initStore();
    }

    componentWillUnmount() {
        this._store.stop();
    }

    downloadDeadletters = () => {
        const dateString = DateTimeService.toCustomUiFormat(DateTimeService.now(), DateTime.jsonDateFormat);
        const fileName = `Deadletters_${dateString}.json`;

        void downloadService.downloadFile(ApiUrls.ServiceDownloadDeadlettersUrl + '?queueName=' + encodeURIComponent(this._queueName), null, fileName);
    };

    renderIcon(id: string) {
        const messagesCount = this._store.data?.messagesCount;
        const deadletterMessagesCount = this._store.data?.deadletterMessagesCount;
        const messagesPerMinuteIn = this._store.data?.messagesPerMinuteIn;
        const queueName = this._store.data?.queueName;
        const errorMessage = this._store.data?.errorMessage;

        const messagesCountText = messagesCount == null ? '?' : messagesCount || 0;
        const deadletterMessagesCountText = deadletterMessagesCount == null ? '?' : deadletterMessagesCount || 0;

        const processingTimeHours = messagesCount == null || messagesPerMinuteIn == null ? '?' : Math.floor(messagesCount / messagesPerMinuteIn / 60) || 0;
        const processingTimeMinutes = messagesCount == null || messagesPerMinuteIn == null ? '?' : Math.floor((messagesCount / messagesPerMinuteIn) % 60) || 0;

        const detailed = messagesPerMinuteIn && messagesCountText
            ? ' / ' + messagesPerMinuteIn + 'mes/min / ' + +processingTimeHours + 'h' + ' ' + processingTimeMinutes + 'm'
            : '';
        const text = messagesCountText + ' / ' + deadletterMessagesCountText + detailed;
        const tooltipText = errorMessage ? `${queueName}: ${errorMessage}` : queueName;

        return (
            <div className="self-test-content">
                <Icon name={'envelope'} />
                {queueName && <ToolTipItem text={tooltipText} targetId={id} />}
                {this._store.dataLoading.isCompleted && (
                    <span className="self-test-message">
                        {text}
                    </span>
                )}
            </div>
        );
    }

    render() {
        if (!this._store.data) return <SelfTestBadge loading loadingIcon="envelope" loadingWithText />;

        return <SelfTestBadge>{this._store.data.deadletterMessagesCount ? this._renderDropDown() : this._renderButton()}</SelfTestBadge>;
    }

    private get _id(): string {
        return `self-test-queue-${this.props.queueName.replace(/\W+/g, '-')}`;
    }

    private get _queueName(): string {
        return this._store.data?.queueName ?? '';
    }

    private _renderDropDown() {
        const { color, isDropdownOpen, toggleDropdown } = this._store;
        const id = this._id;
        return (
            <ButtonDropdown isOpen={isDropdownOpen} toggle={toggleDropdown} size="sm">
                <DropdownToggle caret color={color} id={id} disabled={!this._store.dataLoading.isCompleted}>
                    {this.renderIcon(id)}
                </DropdownToggle>
                <DropdownMenu>
                    <DropdownItem onClick={this.downloadDeadletters}>Download deadletters (top 100)</DropdownItem>
                    <DropdownItem onClick={this._openManageDeadlettersDialog}>Manage deadletter(s)...</DropdownItem>
                </DropdownMenu>
            </ButtonDropdown>
        );
    }

    private _renderButton() {
        const { color } = this._store;
        const id = this._id;
        return (
            <Button id={id} size="sm" className="self-test-button" color={color} disabled={!this._store.dataLoading.isCompleted}>
                {this.renderIcon(id)}
            </Button>
        );
    }

    private _openManageDeadlettersDialog = async () => {
        await modalService.show(ManageMessagesDialog, { queueName: this._queueName });
    };
}

class SelfTestQueueStore {
    @observable public data: QueueStatisticsModel | null = null;
    @observable public isDropdownOpen: boolean = false;
    @observable public dataLoading: PromiseCompletion = new PromiseCompletion(CompletionType.Completed);
    private _timer: Timer = new Timer();
    private readonly _queueName: string;

    constructor(queueName: string) {
        this._queueName = queueName;
    }

    public initStore() {
        this._timer.initRequestInterval(this.loadData);
    }

    @computed
    get color(): string {
        if (!this.dataLoading.isCompleted) return 'secondary';

        let result = 'danger';

        if (!this.data) return result;

        switch (this.data.status) {
            case SelfTestResultStatus.Success:
                result = 'success';
                break;
            case SelfTestResultStatus.Warning:
                result = 'warning';
                break;
        }

        if (this.data.deadletterMessagesCount && this.data.deadletterMessagesCount > 0) return 'danger';

        return result;
    }

    public stop() {
        this._timer.stop();
    }

    @action.bound
    public toggleDropdown() {
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    @action.bound
    public async loadData() {
        const dataRequest = ApiService.post<QueueStatisticsModel>(ApiUrls.ServiceQueueStatisticsUrl, { queueName: this._queueName });
        this.dataLoading.subscribe(dataRequest);

        this.data = (await dataRequest).data;
    }
}
